import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { API } from '@core/constants';
import { StorageType } from '@core/enums';
import { CommonResponse } from '@core/models';
import { AuthPhoneRequest, LoginResponse, User } from '@core/models/auth.model';
import { StorageService } from '@core/services/storage.service';
import { CommonUtil } from '@core/utils';
import { AuthService } from '@shared/services/auth.service';
import { LocaleService } from '@shared/services/locale.service';
import { Observable, map, tap } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class AuthApi {
	constructor(
		private readonly http: HttpClient,
		private readonly storageService: StorageService,
		private readonly localeService: LocaleService,
		private readonly authService: AuthService,
		private readonly router: Router
	) {}

	private readonly platform = 'web';

	/**
	 * Password less login with Firebase and Twilio phone authentication.
	 *
	 * @link https://github.com/DevCenterHouse/kurd-shopping/wiki/APIs#password-less-login
	 * @param request The request payload.
	 *
	 * @returns An Observable of `User` object.
	 */
	login(request: AuthPhoneRequest): Observable<User> {
		return this.http
			.post<LoginResponse>(API.auth.login, {
				...request,
				platform: this.platform,
				lang: this.localeService.lang,
			})
			.pipe(
				tap((loginResponse: LoginResponse) => {
					if (loginResponse.status === '0') {
						throw new Error(loginResponse.message);
					}
				}),
				map((loginResponse: LoginResponse) => {
					const user = { ...loginResponse.response };

					return {
						...user,
						/**
						 * HACK: This is just a quick fix to handle the empty value of `userType`.
						 * If the `userType` value is empty string it will be always equals to `0`.
						 * This should be handled in the server side.
						 */
						userType: user.userType === '' ? '0' : user.userType,
					};
				}),
				tap((userResponse: User) => {
					const { token, phone, phonePrefix, ...user } = { ...userResponse };

					this.storageService.setItem(StorageType.USER, JSON.stringify(user));
					this.storageService.setItem(StorageType.JWT_TOKEN, token);
					this.storageService.setItem(StorageType.PHONE, phone);
					this.storageService.setItem(StorageType.PHONE_PREFIX, phonePrefix);
				})
			);
	}

	/**
	 * Update the user's mobile number.
	 *
	 * @param request The request payload.
	 *
	 * @returns An Observable of `CommonResponse` object.
	 */
	updateMobile(request: AuthPhoneRequest): Observable<CommonResponse> {
		return this.http
			.patch<CommonResponse>(API.auth.updateMobile, {
				...request,
				lang: this.localeService.lang,
				platform: this.platform,
			})
			.pipe(
				tap((response: CommonResponse) => {
					if (response.status === '0') {
						throw new Error(response.message);
					} else {
						const newUser = {
							...this.authService.user(),
							phone: request.phone,
							phonePrefix: request.phonePrefix,
						};

						this.authService.setUser(newUser);
						this.storageService.setItem(StorageType.USER, JSON.stringify(newUser));
						this.storageService.setItem(StorageType.PHONE, newUser.phone);
						this.storageService.setItem(StorageType.PHONE_PREFIX, newUser.phonePrefix);
					}
				})
			);
	}

	/**
	 * Check if phone number is already registered.
	 *
	 * @param request The request payload.
	 *
	 * @returns An Observable of boolean if the phone number is existed or not.
	 */
	checkPhoneRegistered(request: AuthPhoneRequest): Observable<boolean> {
		return this.http
			.post<CommonResponse>(API.auth.checkPhoneRegistered, {
				...request,
				lang: this.localeService.lang,
			})
			.pipe(map((response: CommonResponse) => response.status === '0'));
	}

	checkNumberIsValid(request: AuthPhoneRequest): Observable<CommonResponse> {
		return this.http.post<CommonResponse>(API.auth.validatePhone, {
			...request,
			lang: this.localeService.lang,
		});
	}

	/**
	 * Delete the user's account.
	 *
	 * @param request The request payload.
	 *
	 * @returns An Observable of `CommonResponse` object.
	 */
	deleteAccount(request: AuthPhoneRequest): Observable<CommonResponse> {
		return this.http
			.delete<CommonResponse>(API.auth.deleteAccount, {
				params: CommonUtil.createHttpParams({
					...request,
					lang: this.localeService.lang,
				}),
			})
			.pipe(
				tap((response: CommonResponse) => {
					if (response.status === '0') {
						throw new Error(response.message);
					}

					this.authService.logout();
					this.router.navigate(['/']);
				})
			);
	}

	/**
	 * Send an OTP using Twilio.
	 *
	 * @param request The request payload.
	 *
	 * @returns An Observable of `CommonResponse` object.
	 */
	twilioSendOtp(request: AuthPhoneRequest): Observable<CommonResponse> {
		return this.http
			.post<CommonResponse>(`${API.auth.twilioSendOtp}`, {
				...request,
				lang: this.localeService.lang,
			})
			.pipe(
				tap((response: CommonResponse) => {
					if (response.status === '0') {
						throw new Error(response.message);
					}
				})
			);
	}

	/**
	 * Verify the phone number with Firebase access token or OTP code from Twilio.
	 *
	 * @param request The request payload.
	 *
	 * @returns An Observable of `CommonResponse` object.
	 */
	verifyOtp(request: AuthPhoneRequest): Observable<CommonResponse> {
		return this.http
			.post<CommonResponse>(API.auth.verifyOtp, {
				...request,
				platform: this.platform,
				lang: this.localeService.lang,
			})
			.pipe(
				tap((response: CommonResponse) => {
					if (response.status === '0') {
						throw new Error(response.message);
					}
				})
			);
	}
}
